/**
 * <tkc-dispatch-note> keeps the fulfilment/delivery message in sync with the
 * selected variant.
 *
 * Why a fetch: the selected variant's `custom.dispatch_source` metafield is not
 * reliably re-resolved in the theme's variant-change re-render. This section
 * rendered for a specific `?variant=…&section_id=…` IS deterministic, so on a
 * variant change we fetch that and swap in its message.
 *
 * We trigger on the picker's `change` (fires immediately, so the note updates fast)
 * and also on `variant:update` (a fallback that fires late on heavy pages). The
 * variant id is read from the product form's selected `id` input after a microtask
 * (so the theme has set it), falling back to the event's `resource.id`. A last-id
 * guard skips duplicate fetches; a request token drops out-of-order responses.
 */
class TkcDispatchNote extends HTMLElement {
  #token = 0;
  #lastId = null;

  connectedCallback() {
    this.section = this.closest('.shopify-section, dialog');
    this.sectionId = this.section?.id ? this.section.id.replace('shopify-section-', '') : null;
    this.section?.addEventListener('change', this.#onChange);
    this.section?.addEventListener('variant:update', this.#onVariantUpdate);
  }

  disconnectedCallback() {
    this.section?.removeEventListener('change', this.#onChange);
    this.section?.removeEventListener('variant:update', this.#onVariantUpdate);
  }

  #onChange = () => {
    // Let the theme's own change handler set the form's variant id first.
    setTimeout(() => {
      const form = this.section?.querySelector('form[action*="/cart/add"]');
      const id = form?.querySelector('[name="id"]')?.value;
      if (id) this.#update(id);
    }, 0);
  };

  #onVariantUpdate = (event) => {
    const id = event.detail?.resource?.id;
    if (id != null) this.#update(id);
  };

  async #update(variantId) {
    const textEl = this.querySelector('.tkc-dispatch-note__text');
    if (!textEl || !this.sectionId || String(variantId) === String(this.#lastId)) return;
    this.#lastId = String(variantId);

    const token = ++this.#token;
    try {
      const url = `${window.location.pathname}?variant=${encodeURIComponent(
        variantId
      )}&section_id=${encodeURIComponent(this.sectionId)}`;
      const response = await fetch(url);
      if (!response.ok || token !== this.#token) return;

      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const freshRoot = doc.querySelector('tkc-dispatch-note');
      const freshText = freshRoot?.querySelector('.tkc-dispatch-note__text');
      if (freshText && token === this.#token) {
        textEl.textContent = freshText.textContent.trim();
        this.classList.toggle(
          'tkc-dispatch-note--preorder',
          freshRoot.classList.contains('tkc-dispatch-note--preorder')
        );
      }
    } catch {
      /* leave the current message on a network error */
    }
  }
}

if (!customElements.get('tkc-dispatch-note')) {
  customElements.define('tkc-dispatch-note', TkcDispatchNote);
}
